import { useState } from "react";
import { Search as SearchIcon, X } from "lucide-react";
import { cn } from "../lib/utils";

// Top-bar controls shared by the tracker and the read-only archive view.

/** Borderless icon button used for search / Sankey / back in the top bar. */
export const HEADER_ICON_BUTTON =
  "grid h-9 w-9 flex-none place-items-center rounded-[9px] text-melt-text2 transition-colors hover:bg-melt-text1/[.06] hover:text-melt-text1";

// Expanding search in the top bar: icon only until clicked, collapses when
// left empty. It keeps its own text state: the top bar re-renders a beat after
// the page, so a controlled input there would drop fast keystrokes.
export function TrackerSearch({
  onQueryChange,
  label = "Search applications",
  placeholder = "Search company or role",
}: {
  onQueryChange: (query: string) => void;
  label?: string;
  placeholder?: string;
}) {
  // Open/closed is local too, so the input appears on the same render as the click.
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const update = (next: string) => {
    setValue(next);
    onQueryChange(next);
  };

  return (
    <div
      className={cn(
        "flex h-9 items-center overflow-hidden rounded-[9px] transition-[width,background-color] duration-[220ms] ease-out",
        open ? "w-60 bg-melt-text1/[.05]" : "w-9"
      )}
    >
      <button
        type="button"
        aria-label={label}
        onClick={() => setOpen(true)}
        className={cn(
          "grid h-9 w-9 flex-none place-items-center rounded-[9px] text-melt-text2 transition-colors hover:text-melt-text1",
          !open && "hover:bg-melt-text1/[.06]"
        )}
      >
        <SearchIcon size={17} strokeWidth={1.75} />
      </button>
      {open && (
        <>
          <input
            autoFocus
            value={value}
            onChange={(e) => update(e.target.value)}
            onBlur={() => !value.trim() && setOpen(false)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                update("");
                setOpen(false);
              }
            }}
            placeholder={placeholder}
            className="min-w-0 flex-1 bg-transparent pr-1 text-sm text-melt-text1 outline-none placeholder:text-melt-text3"
          />
          {value && (
            <button
              type="button"
              aria-label="Clear search"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => update("")}
              className="mr-2 grid h-5 w-5 flex-none place-items-center rounded text-melt-text3 hover:text-melt-text1"
            >
              <X size={14} />
            </button>
          )}
        </>
      )}
    </div>
  );
}

export type ViewMode = "columns" | "rows";

// Text-only switch in the top bar: Board / List on the tracker, Saved jobs / Answer bank on Drafts.
export function TopBarToggle<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string; count?: number }[];
  onChange: (value: T) => void;
}) {
  return (
    <div className="flex rounded-[9px] bg-[#F1EADF] p-[3px]">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          aria-pressed={value === option.value}
          className={cn(
            "whitespace-nowrap rounded-[7px] px-2.5 py-1.5 text-[13px] font-medium text-melt-text3 transition-colors hover:text-melt-text1",
            value === option.value && "bg-melt-paper text-melt-text1 shadow-[0_1px_2px_rgba(92,52,23,.12)]"
          )}
        >
          {option.label}
          {option.count !== undefined && <span className="ml-1.5 font-plex text-[11px] text-melt-text3">{option.count}</span>}
        </button>
      ))}
    </div>
  );
}

const VIEW_MODES: { value: ViewMode; label: string }[] = [
  { value: "columns", label: "Board" },
  { value: "rows", label: "List" },
];

export function ViewModeToggle({ value, onChange }: { value: ViewMode; onChange: (mode: ViewMode) => void }) {
  return <TopBarToggle value={value} options={VIEW_MODES} onChange={onChange} />;
}
